import { AdminScopeSnapshot, AdminScopeType } from '../models/AdminScope';

const ACTIVE_SCOPE_KEY = 'admin_active_scope_type';
const GROUP_SCOPE_KEY = 'admin_selected_group_id';
const STORE_SCOPE_KEY = 'admin_selected_store_id';
export const ADMIN_SCOPE_CHANGE_EVENT = 'admin-scope-changed';

const readStorage = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // ignore
  }
};

const emitScopeChange = (snapshot: AdminScopeSnapshot) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ADMIN_SCOPE_CHANGE_EVENT, { detail: snapshot }));
};

let cachedSnapshot: AdminScopeSnapshot | null = null;

const buildSnapshot = (): AdminScopeSnapshot => ({
  activeScopeType: (readStorage(ACTIVE_SCOPE_KEY) as AdminScopeType | null) || 'store',
  selectedGroupId: readStorage(GROUP_SCOPE_KEY),
  selectedStoreId: readStorage(STORE_SCOPE_KEY),
});

const persistSnapshot = (snapshot: AdminScopeSnapshot) => {
  cachedSnapshot = snapshot;
  writeStorage(ACTIVE_SCOPE_KEY, snapshot.activeScopeType);
  writeStorage(GROUP_SCOPE_KEY, snapshot.selectedGroupId);
  writeStorage(STORE_SCOPE_KEY, snapshot.selectedStoreId);
  emitScopeChange(snapshot);
};

export const adminScopeService = {
  getSnapshot(): AdminScopeSnapshot {
    if (cachedSnapshot) {
      return cachedSnapshot;
    }
    cachedSnapshot = buildSnapshot();
    return cachedSnapshot;
  },

  setSnapshot(partial: Partial<AdminScopeSnapshot>) {
    const next: AdminScopeSnapshot = {
      ...adminScopeService.getSnapshot(),
      ...partial,
    };
    persistSnapshot(next);
  },

  setActiveScopeType(activeScopeType: AdminScopeType) {
    adminScopeService.setSnapshot({ activeScopeType });
  },

  setSelectedGroupId(selectedGroupId: string | null) {
    adminScopeService.setSnapshot({ selectedGroupId });
  },

  setSelectedStoreId(selectedStoreId: string | null) {
    adminScopeService.setSnapshot({ selectedStoreId });
  },

  getCurrentStoreId(): string | null {
    return adminScopeService.getSnapshot().selectedStoreId;
  },

  getCurrentGroupId(): string | null {
    return adminScopeService.getSnapshot().selectedGroupId;
  },

  clear() {
    persistSnapshot({
      activeScopeType: 'global',
      selectedGroupId: null,
      selectedStoreId: null,
    });
  },
};
